import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { apiService } from '../../services/api'

type AuthState = 'checking' | 'authenticated' | 'anonymous' | 'subscription-required'

interface MeData {
  authenticated: boolean
  user?: {
    needs_onboarding?: boolean
    is_platform_admin?: boolean
    subscription?: {
      status: string
    }
  }
}

const BLOCKING_SUBSCRIPTION_STATUSES = ['pending_payment', 'expired', 'canceled', 'failed', 'none']

export default function ProtectedRoute() {
  const location = useLocation()
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [meData, setMeData] = useState<MeData | null>(null)

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      setAuthState('anonymous')
      return
    }

    let active = true
    apiService
      .getMe()
      .then((response) => {
        if (!active) return
        const data: MeData = response.data?.data || { authenticated: false }
        setMeData(data)

        if (!data.authenticated) {
          setAuthState('anonymous')
          return
        }

        const subStatus = data.user?.subscription?.status
        if (subStatus && BLOCKING_SUBSCRIPTION_STATUSES.includes(subStatus)) {
          setAuthState('subscription-required')
        } else {
          setAuthState('authenticated')
        }
      })
      .catch((err) => {
        if (!active) return
        const status = err?.response?.status
        if (status === 401) {
          localStorage.removeItem('auth_token')
          localStorage.removeItem('refresh_token')
          setAuthState('anonymous')
        } else if (status === 403) {
          const code = err?.response?.data?.code
          if (code?.startsWith('SUBSCRIPTION_')) {
            setAuthState('subscription-required')
          } else {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('refresh_token')
            setAuthState('anonymous')
          }
        } else {
          setAuthState('anonymous')
        }
      })

    return () => {
      active = false
    }
  }, [])

  if (authState === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="rounded-lg border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-600 shadow-sm">
          Validando sessão...
        </div>
      </div>
    )
  }

  if (authState === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  // Redirect to onboarding if needed (but not if already there)
  if (meData?.user?.needs_onboarding && location.pathname !== '/app/onboarding') {
    return <Navigate to="/app/onboarding" replace />
  }

  // Redirect to billing if subscription is blocking (but allow billing and onboarding pages)
  if (authState === 'subscription-required' && location.pathname !== '/app/billing' && location.pathname !== '/app/onboarding') {
    return <Navigate to="/app/billing" replace />
  }

  return <Outlet />
}